import { Fragment, lazy, Suspense } from 'react';
import { generatePath } from 'react-router';
import { observer } from 'mobx-react';
import cls from 'classnames';
import { useToggle } from '@cutwise/react-utils';

import { BILLING_DASHBOARD_PAGE } from '@/billing/constants/pages';
import Avatar from '@/common/components/Avatar/Avatar';
import MenuItemSeparator from '@/common/components/MenuList/MenuItemSeparator/MenuItemSeparator';
import { ClientSetting } from '@/common/constants/clientSettings';
import {
  CONTACTS_PAGE,
  MULTI_PROJECTS_PAGE,
  SHARED_MULTI_PROJECTS_PAGE,
  TASKS_PAGE,
  WIDGET_CONSTRUCTOR_PAGE,
} from '@/common/constants/pages';
import {
  CAN_ACCESS_ADMIN_PAGE,
  CAN_EDIT_MULTISTAGE,
  LGD,
  MANAGE_CONTACTS,
  MANAGER,
  securityManager,
  VIEW_STOCK,
} from '@/common/services/SecurityManager';
import URLProvider from '@/common/services/URLProvider';
import { SERVICES_PAGE } from '@/conversion/constants/pages';
import { User } from '@/user/entity/User';

import AnimatedDropdown from '../../AnimatedDropdown/AnimatedDropdown';
import Icon from '../../Icon/Icon';
import Link, { NONE } from '../../Link/Link';
import MenuItem from '../../MenuList/MenuItem/MenuItem';
import MenuList from '../../MenuList/MenuList';
import styles from '../Header.module.css';

export interface UserNavDropdownProps {
  user: User;
  logout: () => void;
}

const ProductFormImport = lazy(() => import('@/product/components/ProductForm/ProductFormImport/ProductFormImport'));

// this component has duplicated functionality with sideBarTreeSectionsConfig
// if new elements are added, they should be added there, too.
function UserNavDropdown(props: UserNavDropdownProps) {
  const { user, logout } = props;
  const [isImportModalOpened, toggleImportModal, closeImportModal] = useToggle();

  return (
    <Fragment>
      <AnimatedDropdown
        aria-label="User menu"
        className={cls(styles.headerLink, styles.headerLinkDesktop)}
        label={<Avatar user={user} withUsername />}
      >
        <MenuList>
          <MenuItem>
            <Link aria-label="My Profile" activeClassName={styles.active} appearance={NONE} exact to={URLProvider.profile(user)}>
              <Icon name="user-profile" /> My Profile
            </Link>
          </MenuItem>

          {!user?.hasSetting(ClientSetting.CustomBilling) && (
            <MenuItem>
              <Link aria-label="Billing Dashboard" activeClassName={styles.active} appearance={NONE} exact to={BILLING_DASHBOARD_PAGE}>
                <Icon name="user-billing-eur" />

                <span> Billing Dashboard</span>
              </Link>
            </MenuItem>
          )}

          <MenuItemSeparator>Multistage Projects:</MenuItemSeparator>

          {securityManager.isGranted(CAN_EDIT_MULTISTAGE) && (
            <MenuItem>
              <Link aria-label="Multistage projects owned by me" activeClassName={styles.active} appearance={NONE} exact to={MULTI_PROJECTS_PAGE}>
                <Icon name="multistage" /> Owned by Me
              </Link>
            </MenuItem>
          )}

          <MenuItem>
            <Link aria-label="Multistage projects shared with me" activeClassName={styles.active} appearance={NONE} exact to={SHARED_MULTI_PROJECTS_PAGE}>
              <Icon name="multistage-shared" /> Shared With Me
            </Link>
          </MenuItem>

          <MenuItemSeparator>Projects:</MenuItemSeparator>

          {securityManager.isGranted(VIEW_STOCK) && (
            <MenuItem>
              <Link aria-label="Projects owned by me" activeClassName={styles.active} appearance={NONE} exact to="/projects">
                <Icon name="project" /> Owned by Me
              </Link>
            </MenuItem>
          )}

          <MenuItem>
            <Link aria-label="Projects shared with me" activeClassName={styles.active} appearance={NONE} exact to="/shared-with-me-projects">
              <Icon name="project-shared" /> Shared with Me
            </Link>
          </MenuItem>

          {securityManager.isGranted(VIEW_STOCK) && (
            <Fragment>
              {!securityManager.isGranted(LGD) && (
                <Fragment>
                  <MenuItemSeparator>My Natural Diamonds:</MenuItemSeparator>

                  <MenuItem>
                    <Link aria-label="My natural colorless" activeClassName={styles.active} appearance={NONE} exact to="/catalog/diamond-colorless/edit">
                      <Icon name="diamond" />
                      Colorless
                    </Link>
                  </MenuItem>

                  <MenuItem>
                    <Link aria-label="My natural fancy color" activeClassName={styles.active} appearance={NONE} exact to="/catalog/diamond-fancy/edit">
                      <Icon className={styles.iconFancy} name="diamond" />
                      Fancy Color
                    </Link>
                  </MenuItem>

                  <MenuItem>
                    <Link aria-label="My natural rough" activeClassName={styles.active} appearance={NONE} exact to="/catalog/rough/edit">
                      <Icon name="rough" />
                      Rough
                    </Link>
                  </MenuItem>

                  <MenuItemSeparator>Other:</MenuItemSeparator>

                  <MenuItem>
                    <Link aria-label="My jewelry" activeClassName={styles.active} appearance={NONE} exact to="/catalog/jewelry/edit">
                      <Icon name="jewelry" />
                      Jewelry
                    </Link>
                  </MenuItem>

                  <MenuItem>
                    <Link aria-label="My gems" activeClassName={styles.active} appearance={NONE} exact to="/catalog/gem/edit">
                      <Icon name="gem" />
                      Gems
                    </Link>
                  </MenuItem>

                  <MenuItemSeparator />
                </Fragment>
              )}

              {securityManager.isGranted(LGD) && (
                <Fragment>
                  <MenuItemSeparator>Lab-grown diamonds:</MenuItemSeparator>

                  <MenuItem>
                    <Link aria-label="My lab-grown colorless" activeClassName={styles.active} appearance={NONE} exact to="/catalog/lgd-colorless/edit">
                      <Icon name="diamond" />
                      Colorless
                    </Link>
                  </MenuItem>

                  <MenuItem>
                    <Link aria-label="My lab-grown fancy color" activeClassName={styles.active} appearance={NONE} exact to="/catalog/lgd-fancy/edit">
                      <Icon className={styles.iconFancy} name="diamond" />
                      Fancy Color
                    </Link>
                  </MenuItem>

                  <MenuItem>
                    <Link aria-label="My lab-grown rough" activeClassName={styles.active} appearance={NONE} exact to="/catalog/lgd-rough/edit">
                      <Icon name="rough" />
                      Rough
                    </Link>
                  </MenuItem>

                  <MenuItemSeparator />
                </Fragment>
              )}

              {securityManager.isGranted(MANAGER) && (
                <MenuItem aria-label="Import Products" onClick={toggleImportModal}>
                  <Icon name="cloud-upload" /> Import Products
                </MenuItem>
              )}
            </Fragment>
          )}

          <MenuItemSeparator>Services:</MenuItemSeparator>

          <MenuItem>
            <Link aria-label="Services" activeClassName={styles.active} appearance={NONE} exact to={SERVICES_PAGE}>
              <Icon name="file-conversion" />

              <span> Services</span>
            </Link>
          </MenuItem>

          <MenuItem>
            <Link aria-label="My Tasks" activeClassName={styles.active} appearance={NONE} exact to={TASKS_PAGE}>
              <Icon name="pending" />

              <span> My Tasks</span>
            </Link>
          </MenuItem>

          {securityManager.isGranted(MANAGER) && (
            <MenuItem>
              <Link aria-label="Widget Builder" appearance={NONE} end to={generatePath(WIDGET_CONSTRUCTOR_PAGE)}>
                <Icon name="widgets" /> Widget Builder
              </Link>
            </MenuItem>
          )}

          {securityManager.isGranted(CAN_ACCESS_ADMIN_PAGE) && (
            <MenuItem>
              <Link aria-label="Admin" appearance={NONE} href={URLProvider.adminPage()}>
                <Icon name="full-protect" /> Admin
              </Link>
            </MenuItem>
          )}

          {securityManager.isGranted(MANAGE_CONTACTS) && (
            <MenuItem>
              <Link aria-label="Contacts" appearance={NONE} href={generatePath(CONTACTS_PAGE)}>
                <Icon name="private-access-for-users" /> Contacts
              </Link>
            </MenuItem>
          )}

          <MenuItemSeparator />

          <MenuItem aria-label="Log out" onClick={logout}>
            <Icon name="user-logout" />
            Log out
          </MenuItem>
        </MenuList>
      </AnimatedDropdown>

      <Suspense fallback={null}>
        <ProductFormImport onClose={closeImportModal} isOpened={isImportModalOpened} />
      </Suspense>
    </Fragment>
  );
}

export default observer(UserNavDropdown);
